import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { UserSession } from '../../shared/models';
import { normalizeCompanyMembershipRole, normalizeCompanyRoleFromClaim } from '../../features/company-context/utils/company-membership-role.util';
import {
  clearUserProfilePreferences,
  readUserProfilePreferences,
} from '../profile/user-profile-preferences.storage';
import { readCompanyRoleRawFromAccessToken } from '../utils/jwt-access-token-claims.util';
import { parseTaskeoUserRoleValue123 } from '../utils/taskeo-company-role-response.util';
import { BillingGateService } from '../billing-gate/billing-gate.service';
import { CompanyContextService } from '../tenant/company-context.service';

const TOKEN_STORAGE_KEY = 'taskeo.auth.token';
const USER_STORAGE_KEY = 'taskeo.auth.user';
const REFRESH_TOKEN_STORAGE_KEY = 'taskeo.auth.refreshToken';
const ACCESS_EXPIRES_AT_STORAGE_KEY = 'taskeo.auth.accessExpiresAtUtc';
const REFRESH_EXPIRES_AT_STORAGE_KEY = 'taskeo.auth.refreshExpiresAtUtc';
const PROFILE_PREFS_OWNER_KEY = 'taskeo.auth.profilePreferencesOwnerUserId';

export interface AuthSessionTokenMeta {
  refreshToken?: string;
  expiresAtUtc?: string;
  refreshTokenExpiresAtUtc?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly currentUserSignal = signal<UserSession | null>(null);
  private readonly tokenSignal = signal<string | null>(null);
  private readonly injector = inject(Injector);

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly user = this.currentUser;
  readonly token = this.tokenSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.tokenSignal());

  constructor(private readonly router: Router) {}

  restoreSession(): void {
    this.tokenSignal.set(this.readToken());
    const user = this.readUser();
    if (user) {
      this.ensureProfilePreferencesOwnership(user.id);
    }
    this.currentUserSignal.set(user ? this.mergeProfileAvatar(user) : null);
    if (!this.readToken()) {
      this.clearRefreshSideStorage();
    } else {
      this.applyRoleFromCurrentAccessToken();
    }
  }

  setSession(token: string, session: UserSession, tokenMeta?: AuthSessionTokenMeta): void {
    this.ensureProfilePreferencesOwnership(session.id);
    const merged = this.mergeProfileAvatar(session);
    this.tokenSignal.set(token);
    this.currentUserSignal.set(merged);
    this.storeToken(token);
    this.storeUser(merged);
    if (tokenMeta?.refreshToken?.trim()) {
      this.storeRefreshToken(tokenMeta.refreshToken.trim());
    }
    if (tokenMeta?.expiresAtUtc?.trim()) {
      this.storeAccessExpiresAtUtc(tokenMeta.expiresAtUtc.trim());
    }
    if (tokenMeta?.refreshTokenExpiresAtUtc?.trim()) {
      this.storeRefreshExpiresAtUtc(tokenMeta.refreshTokenExpiresAtUtc.trim());
    }
  }

  /**
   * Resposta de POST /api/auth/refresh (LoginResponse). Rotação: substitui access + refresh no storage.
   * Não altera o utilizador em memória.
   */
  applyRefreshResponse(raw: unknown): boolean {
    const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const access = typeof r['accessToken'] === 'string' ? r['accessToken'].trim() : '';
    const refresh = typeof r['refreshToken'] === 'string' ? r['refreshToken'].trim() : '';
    if (!access || !refresh) return false;

    this.tokenSignal.set(access);
    this.storeToken(access);
    this.storeRefreshToken(refresh);

    const exp = typeof r['expiresAtUtc'] === 'string' ? r['expiresAtUtc'].trim() : '';
    if (exp) this.storeAccessExpiresAtUtc(exp);
    else this.clearAccessExpiresAtUtc();

    const rexp = typeof r['refreshTokenExpiresAtUtc'] === 'string' ? r['refreshTokenExpiresAtUtc'].trim() : '';
    if (rexp) this.storeRefreshExpiresAtUtc(rexp);
    else this.clearRefreshExpiresAtUtc();

    this.applyCompanyRoleFromAccessToken(access);
    return true;
  }

  /**
   * Após sign-in (JSON sem `role`) ou refresh (só tokens): lê claim no access JWT e actualiza a sessão.
   */
  applyRoleFromCurrentAccessToken(): void {
    const t = this.getToken();
    if (t) this.applyCompanyRoleFromAccessToken(t);
  }

  /**
   * Quando o JWT tem claim de papel, torna-se fonte de verdade (inclui despromoções após refresh).
   */
  private applyCompanyRoleFromAccessToken(accessToken: string): void {
    const raw = readCompanyRoleRawFromAccessToken(accessToken);
    if (raw === undefined) return;
    const user = this.currentUserSignal();
    if (!user) return;
    const cur = normalizeCompanyMembershipRole(user.role, 'Member');
    const jwtNorm = normalizeCompanyRoleFromClaim(raw, cur);
    const fromNum = parseTaskeoUserRoleValue123(raw);
    const nextRoleValue =
      fromNum ??
      (jwtNorm === 'Owner' ? 1 : jwtNorm === 'Admin' ? 2 : jwtNorm === 'Member' ? 3 : undefined);

    const roleUnchanged = jwtNorm === cur;
    const valueUnchanged =
      nextRoleValue === undefined || nextRoleValue === user.roleValue;
    if (roleUnchanged && valueUnchanged) return;

    this.setCurrentUser({
      ...user,
      ...(!roleUnchanged ? { role: jwtNorm } : {}),
      ...(nextRoleValue !== undefined ? { roleValue: nextRoleValue } : {}),
    });
  }

  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  logout(): void {
    this.tokenSignal.set(null);
    this.currentUserSignal.set(null);
    this.clearStorage();
    this.injector.get(CompanyContextService).resetContext();
    // Garante que o gate de billing não vaze entre sessões/empresas.
    this.injector.get(BillingGateService).clear();
    void this.router.navigate(['/auth/login']);
  }

  getToken(): string | null {
    return this.tokenSignal();
  }

  setCurrentUser(user: UserSession): void {
    const merged = this.mergeProfileAvatar(user);
    this.currentUserSignal.set(merged);
    this.storeUser(merged);
  }

  private readToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private readUser(): UserSession | null {
    try {
      const raw = localStorage.getItem(USER_STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as UserSession;
    } catch {
      return null;
    }
  }

  private storeToken(token: string): void {
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {}
  }

  private storeUser(user: UserSession): void {
    try {
      const { avatarUrl: _drop, ...rest } = user;
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(rest));
    } catch {}
  }

  private clearStorage(): void {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      localStorage.removeItem(PROFILE_PREFS_OWNER_KEY);
      this.clearRefreshSideStorage();
      clearUserProfilePreferences();
    } catch {}
  }

  /**
   * Garante que as preferências locais (incluindo avatar) pertencem ao usuário atual.
   * Quando troca de conta no mesmo dispositivo, descarta as preferências do dono anterior
   * para evitar que avatar/telefone/etc. de outra conta vazem para a sessão nova.
   */
  private ensureProfilePreferencesOwnership(userId: string | undefined | null): void {
    const current = (userId ?? '').trim();
    if (!current) return;
    let previous = '';
    try {
      previous = (localStorage.getItem(PROFILE_PREFS_OWNER_KEY) ?? '').trim();
    } catch {
      previous = '';
    }
    if (previous === current) return;
    if (previous) {
      clearUserProfilePreferences();
    }
    try {
      localStorage.setItem(PROFILE_PREFS_OWNER_KEY, current);
    } catch {}
  }

  private storeRefreshToken(value: string): void {
    try {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, value);
    } catch {}
  }

  private storeAccessExpiresAtUtc(value: string): void {
    try {
      localStorage.setItem(ACCESS_EXPIRES_AT_STORAGE_KEY, value);
    } catch {}
  }

  private storeRefreshExpiresAtUtc(value: string): void {
    try {
      localStorage.setItem(REFRESH_EXPIRES_AT_STORAGE_KEY, value);
    } catch {}
  }

  private clearAccessExpiresAtUtc(): void {
    try {
      localStorage.removeItem(ACCESS_EXPIRES_AT_STORAGE_KEY);
    } catch {}
  }

  private clearRefreshExpiresAtUtc(): void {
    try {
      localStorage.removeItem(REFRESH_EXPIRES_AT_STORAGE_KEY);
    } catch {}
  }

  private clearRefreshSideStorage(): void {
    try {
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ACCESS_EXPIRES_AT_STORAGE_KEY);
      localStorage.removeItem(REFRESH_EXPIRES_AT_STORAGE_KEY);
    } catch {}
  }

  /** Avatar guardado nas preferências locais (data URL / https) entra na sessão para a UI. */
  private mergeProfileAvatar(user: UserSession): UserSession {
    const prefs = readUserProfilePreferences();
    const fromPrefs = prefs?.avatarUrl?.trim();
    if (!fromPrefs) return user;
    return { ...user, avatarUrl: fromPrefs };
  }
}
